// client/src/components/admin/FederationSettings.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Switch, FormControlLabel,
  Snackbar, Alert, Paper, Divider, CircularProgress,
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../../axiosInstance';
import AppBarHeader from '../AppBarHeader';

const DEFAULTS = {
  'enableFederation': false,
  'federation.domain': '',
  'federation.instanceName': '',
  'federation.instanceDescription': '',
  'federation.allowlistMode': false,
  'federation.shareFinancialDataDefault': true,
};

export default function FederationSettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;

  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingKeys, setGeneratingKeys] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    axiosInstance.get('/admin/federation-settings')
      .then(r => setSettings({ ...DEFAULTS, ...r.data }))
      .catch(() => setSnackbar({ open: true, message: 'Failed to load settings', severity: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (key, value) => setSettings(s => ({ ...s, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/admin/federation-settings', settings);
      setSnackbar({ open: true, message: 'Federation settings saved', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateKeys = async () => {
    setGeneratingKeys(true);
    try {
      const r = await axiosInstance.post('/admin/federation-generate-keys');
      const { generated, checked } = r.data;
      setSnackbar({
        open: true,
        message: generated === 0
          ? `All ${checked} users already have federation keys`
          : `Generated keys for ${generated} user${generated !== 1 ? 's' : ''} (${checked} total checked)`,
        severity: 'success',
      });
    } catch {
      setSnackbar({ open: true, message: 'Failed to generate federation keys', severity: 'error' });
    } finally {
      setGeneratingKeys(false);
    }
  };

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/login'); };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <>
      <AppBarHeader isAdmin={user?.isAdmin} userId={user?.id} onLogout={handleLogout} />
      <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Button variant="outlined" sx={{ mb: 2 }} onClick={() => navigate('/admin')}>
        ← Back to Admin Dashboard
      </Button>
      <Typography variant="h5" gutterBottom>Federation Settings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enable ActivityPub federation to allow this instance to connect with other InterCollect instances.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Instance Identity</Typography>
        <TextField
          fullWidth label="Instance Domain" sx={{ mb: 2 }}
          value={settings['federation.domain']}
          onChange={e => handleChange('federation.domain', e.target.value)}
          helperText="Public domain advertised in actor URLs (e.g. intercollect.example.com)"
        />
        <TextField
          fullWidth label="Instance Name" sx={{ mb: 2 }}
          value={settings['federation.instanceName']}
          onChange={e => handleChange('federation.instanceName', e.target.value)}
        />
        <TextField
          fullWidth multiline rows={2} label="Instance Description"
          value={settings['federation.instanceDescription']}
          onChange={e => handleChange('federation.instanceDescription', e.target.value)}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Access Controls</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!!settings['federation.allowlistMode']}
              onChange={e => handleChange('federation.allowlistMode', e.target.checked)}
            />
          }
          label="Allowlist mode — only explicitly approved instances can federate"
        />
        <Divider sx={{ my: 2 }} />
        <FormControlLabel
          control={
            <Switch
              checked={!!settings['federation.shareFinancialDataDefault']}
              onChange={e => handleChange('federation.shareFinancialDataDefault', e.target.checked)}
            />
          }
          label="Share financial data by default (purchase/sale prices included in federated items)"
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>User Federation Keys</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each user needs an RSA keypair to sign federated activities. Keys are generated automatically at
          registration, but users who existed before federation was enabled will be missing theirs.
          Run this after enabling federation for the first time.
        </Typography>
        <Button
          variant="outlined"
          onClick={handleGenerateKeys}
          disabled={generatingKeys}
          startIcon={generatingKeys ? <CircularProgress size={16} /> : null}
        >
          {generatingKeys ? 'Generating…' : 'Generate Missing Keys'}
        </Button>
      </Paper>

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save Settings'}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </>
  );
}
